import { useEffect, useRef, useState } from 'react'
import { Button } from '@mui/material'
import { enqueueSnackbar } from 'notistack'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import QRCode from 'react-qr-code'

import appleButton from '/assets/images/add-to-apple-wallet.svg'
import googleButton from '/assets/images/add-to-google-wallet.svg'

const PassEmailCopyActions = ({ appleUrl, googleUrl }) => {
  const [copyMode, setCopyMode] = useState(null)
  const [copyButtonsReady, setCopyButtonsReady] = useState(false)
  const [copyBarcodesReady, setCopyBarcodesReady] = useState(false)
  const [copyButtonImages, setCopyButtonImages] = useState({ apple: appleButton, google: googleButton })
  const [copyBarcodeImages, setCopyBarcodeImages] = useState({ apple: null, google: null })
  const copyContainerRef = useRef(null)

  useEffect(() => {
    const convertSvgToPngDataUrl = async (src, width = 160) => {
      try {
        const image = new Image()
        image.decoding = 'async'

        const loaded = await new Promise((resolve, reject) => {
          image.onload = () => resolve(image)
          image.onerror = reject
          image.src = src
        })

        const ratio = loaded.height / loaded.width
        const canvas = document.createElement('canvas')
        canvas.width = width
        canvas.height = Math.round(width * ratio)
        const context = canvas.getContext('2d')
        context.drawImage(loaded, 0, 0, canvas.width, canvas.height)
        return canvas.toDataURL('image/png')
      }
      catch {
        return src
      }
    }

    const prepare = async () => {
      const [applePng, googlePng] = await Promise.all([
        convertSvgToPngDataUrl(appleButton),
        convertSvgToPngDataUrl(googleButton)
      ])
      setCopyButtonImages({ apple: applePng, google: googlePng })
      setCopyButtonsReady(true)
    }

    prepare()
  }, [])

  useEffect(() => {
    if (copyMode !== 'barcodes' || !appleUrl || !googleUrl) return

    setCopyBarcodesReady(false)
    setCopyBarcodeImages({ apple: null, google: null })

    const makeQrPng = async value => {
      try {
        const svg = [...(copyContainerRef.current?.querySelectorAll('svg[data-qr-value]') ?? [])]
          .find(item => item.getAttribute('data-qr-value') === value)
        if (!svg) return null

        const serializer = new XMLSerializer()
        const svgString = serializer.serializeToString(svg)
        const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' })
        const svgUrl = URL.createObjectURL(svgBlob)

        const image = new Image()
        image.decoding = 'async'
        const loaded = await new Promise((resolve, reject) => {
          image.onload = () => resolve(image)
          image.onerror = reject
          image.src = svgUrl
        })

        const canvas = document.createElement('canvas')
        canvas.width = 154
        canvas.height = 154
        const context = canvas.getContext('2d')
        context.fillStyle = '#ffffff'
        context.fillRect(0, 0, canvas.width, canvas.height)
        context.drawImage(loaded, 0, 0, canvas.width, canvas.height)
        URL.revokeObjectURL(svgUrl)
        return canvas.toDataURL('image/png')
      }
      catch {
        return null
      }
    }

    const prepare = async () => {
      await new Promise(resolve => requestAnimationFrame(resolve))
      const [appleQr, googleQr] = await Promise.all([makeQrPng(appleUrl), makeQrPng(googleUrl)])
      setCopyBarcodeImages({ apple: appleQr, google: googleQr })
      setCopyBarcodesReady(Boolean(appleQr && googleQr))
    }

    prepare()
  }, [appleUrl, copyMode, googleUrl])

  useEffect(() => {
    if (!copyMode || !copyButtonsReady || (copyMode === 'barcodes' && !copyBarcodesReady)) return

    const copy = async () => {
      await new Promise(resolve => requestAnimationFrame(resolve))
      const node = copyContainerRef.current
      if (!node) return

      const fallbackCopy = () => {
        const selection = window.getSelection()
        const range = document.createRange()
        range.selectNodeContents(node)
        selection.removeAllRanges()
        selection.addRange(range)
        const copied = document.execCommand('copy')
        selection.removeAllRanges()
        return copied
      }

      const text = [`Apple Wallet: ${appleUrl ?? ''}`, `Google Wallet: ${googleUrl ?? ''}`].join('\n')
      let copied = false

      try {
        if (navigator.clipboard && window.ClipboardItem) {
          await navigator.clipboard.write([new ClipboardItem({
            'text/html': new Blob([node.innerHTML], { type: 'text/html' }),
            'text/plain': new Blob([text], { type: 'text/plain' })
          })])
          copied = true
        }
      }
      catch (err) {
        console.error('Clipboard API copy failed, falling back to execCommand', err)
      }

      if (!copied) copied = fallbackCopy()
      enqueueSnackbar({ message: copied ? 'Email content copied' : 'Unable to copy email content', variant: copied ? 'success' : 'error' })
      setCopyMode(null)
    }

    copy()
  }, [appleUrl, copyBarcodesReady, copyButtonsReady, copyMode, googleUrl])

  return (
    <>
      <div className='flex flex-row gap-3 flex-wrap justify-center'>
        <Button variant='contained' color='secondary' onClick={() => setCopyMode('barcodes')}>
          <ContentCopyIcon className='mr-2' />Copy email content (with barcodes)
        </Button>
        <Button variant='contained' color='secondary' onClick={() => setCopyMode('links')}>
          <ContentCopyIcon className='mr-2' />Copy email content (no barcodes)
        </Button>
      </div>

      {copyMode && <div ref={copyContainerRef} style={{ position: 'fixed', left: '-10000px', top: 0, backgroundColor: '#fff', padding: 16 }}>
        <table cellPadding={copyMode === 'barcodes' ? 6 : 8} cellSpacing={copyMode === 'barcodes' ? 6 : 8}>
          <tbody>
            <tr>
              <td style={{ textAlign: 'center' }}><a href={appleUrl} target='_blank' rel='noreferrer'><img src={copyButtonImages.apple} width={160} style={{ display: 'block', margin: '0 auto' }} /></a></td>
              <td style={{ textAlign: 'center' }}><a href={googleUrl} target='_blank' rel='noreferrer'><img src={copyButtonImages.google} width={160} style={{ display: 'block', margin: '0 auto' }} /></a></td>
            </tr>
            {copyMode === 'barcodes' && <tr>
              <td style={{ textAlign: 'center' }}><div style={{ backgroundColor: '#fff', width: 160, margin: '0 auto', padding: 3 }}>
                {copyBarcodeImages.apple
                  ? <img src={copyBarcodeImages.apple} width={154} height={154} style={{ display: 'block', margin: '0 auto' }} />
                  : <QRCode size={154} value={appleUrl ?? ''} />}
              </div></td>
              <td style={{ textAlign: 'center' }}><div style={{ backgroundColor: '#fff', width: 160, margin: '0 auto', padding: 3 }}>
                {copyBarcodeImages.google
                  ? <img src={copyBarcodeImages.google} width={154} height={154} style={{ display: 'block', margin: '0 auto' }} />
                  : <QRCode size={154} value={googleUrl ?? ''} />}
              </div></td>
            </tr>}
          </tbody>
        </table>
        {copyMode === 'barcodes' && <div style={{ position: 'absolute', left: -9999, top: 0 }}>
          <QRCode data-qr-value={appleUrl} size={256} value={appleUrl ?? ''} viewBox='0 0 256 256' />
          <QRCode data-qr-value={googleUrl} size={256} value={googleUrl ?? ''} viewBox='0 0 256 256' />
        </div>}
      </div>}
    </>
  )
}

export default PassEmailCopyActions
